/**
 * RecordedResultView
 *
 * The state between recording a cycle's ending and closing the cycle.
 *
 * That state did not exist before: `useRecordCycleOutcome` wrote the outcome and
 * `status: "closed"` in the same PATCH, so typing a beta value ended the cycle
 * instantly and reset the navigation stack out to Care. Recording now writes only
 * the outcome, which leaves the cycle open holding its result — and this is what
 * the patient sees against it: what was recorded, and the action that closes the
 * cycle when they are ready.
 *
 * Keeping the cycle open is also closer to how a beta works. A borderline or
 * first positive value is often followed by a repeat, and a closed cycle cannot
 * carry one, so the result stays editable here.
 *
 * Tone is decided by the ending, never by the surface. `mayCelebrate` gates the
 * confetti and is true only for a recorded pregnancy, so no styling change can
 * make a loss or a cancellation celebratory.
 */

import { useEffect, useRef } from "react";
import { Animated, View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CompactActionButton } from "@louise/moon-ui";
import { ConfettiBurst } from "@louise/moon-ui/primitives/ConfettiBurst";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { CheckCircle2, Pencil } from "lucide-react-native";
import { EMBER } from "../../../../../onboarding/theme";
import type { StepPerspective } from "@src/packages/homepage/sections/TreatmentProgressModal";
import { recordedResultVoice } from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import { useTranslations } from "@src/packages/i18n";

const NS = "care.treatment_hub.recorded_result";

/**
 * The endings this surface can be reached with. Mirrors the outcome `type`
 * values the app can write, so an ending cannot be displayed that could not
 * have been recorded.
 */
export type RecordedResultKind =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other";

export function RecordedResultView({
  kind,
  recordedDate,
  onCloseCycle,
  onUpdateResult,
  onUpdatePregnancy,
  pregnancyStatus,
  isClosing = false,
  perspective = "self",
}: {
  kind: RecordedResultKind;
  /** Formatted for display by the caller, or null when the date is unknown. */
  recordedDate: string | null;
  onCloseCycle: () => void;
  onUpdateResult: () => void;
  /**
   * Say where the pregnancy stands — still going, ended, or a birth.
   *
   * Only ever passed for a recorded pregnancy, and only to the person who carried
   * it: this is where a loss is reported, and a partner reporting one records a
   * fact about someone else's body. Absent, the row is not rendered, so a caller
   * that cannot support it simply does not offer it.
   */
  onUpdatePregnancy?: () => void;
  /**
   * Where the pregnancy stands, as `summarisePregnancyStatus` allows it to be
   * shown — and no more.
   *
   * A partner can see this card, and partner access is all-or-nothing: there is
   * no per-field sharing control, so anything here is visible to him with no way
   * for her to withhold it. Which is why the summary carries the status, the week
   * count and the number of babies, and never the kind of loss. That word is hers,
   * her record's and her clinic's until she can choose who sees it.
   */
  pregnancyStatus?: {
    stands: "continuing" | "ended" | "birth";
    weekCount: number | null;
    liveBirthCount: number | null;
  } | null;
  isClosing?: boolean;
  /**
   * Whose cycle this reads as, matching `WaitingPhaseView` on the same step.
   *
   * A partner on a shared cycle can record the result — the QA persona covers
   * exactly that — so they reach this card, and it must not address them as the
   * person who carried the treatment. Closing the cycle stays with the patient,
   * so the partner view offers the result but not the ending.
   */
  perspective?: StepPerspective;
}) {
  const t = useTranslations();
  const isPartnerView = perspective === "partner";

  /**
   * A birth gets its own heading, rather than the positive result it grew out of.
   *
   * `kind` stays "pregnancy" once a baby is born, so without this the card read
   * "A positive result — Congratulations. It's saved to this cycle, your clinic
   * will confirm what comes next" to someone whose child has been born.
   */
  const voice = recordedResultVoice(kind, pregnancyStatus?.stands);

  // Only a recorded pregnancy may be celebrated. Everything else — including a
  // cancellation and an unclassified ending — arrives quietly.
  const mayCelebrate = kind === "pregnancy";

  // The entrance is the "something just happened" beat that the old flow had no
  // room for: it went from a modal straight to a wiped navigation stack.
  const entrance = useRef(new Animated.Value(0)).current;

  /**
   * Stable 1 on the first render, which is what makes the burst fire.
   *
   * This used to start at 0 and be incremented inside the effect below.
   * Mutating a ref causes no re-render, so `ConfettiBurst` only ever saw 0 —
   * and it returns null at 0. The celebration was dead. `CycleClosingCarousel`
   * has always done it this way.
   */
  const confettiTrigger = useRef(mayCelebrate ? 1 : 0).current;

  useEffect(() => {
    Animated.timing(entrance, {
      toValue: 1,
      duration: 420,
      useNativeDriver: true,
    }).start();
  }, [entrance]);

  return (
    <View style={{ position: "relative" }}>
      {mayCelebrate ? <ConfettiBurst trigger={confettiTrigger} /> : null}

      <Animated.View
        testID="recorded-result-view"
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          padding: 16,
          gap: 12,
          opacity: entrance,
          transform: [
            {
              translateY: entrance.interpolate({
                inputRange: [0, 1],
                outputRange: [10, 0],
              }),
            },
          ],
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 9 }}>
          <CheckCircle2 size={17} color={EMBER.colors.text} strokeWidth={1.8} />
          <Text
            style={{
              fontSize: 15,
              lineHeight: 20,
              color: EMBER.colors.text,
              fontWeight: "700",
            }}
          >
            {t(
              isPartnerView
                ? `${NS}.partner_title_${voice}`
                : `${NS}.title_${voice}`,
            )}
          </Text>
        </View>

        <Text
          style={{
            fontSize: 13,
            lineHeight: 19,
            color: EMBER.colors.textMid,
            fontWeight: "400",
          }}
        >
          {t(
            isPartnerView
              ? `${NS}.partner_body_${voice}`
              : `${NS}.body_${voice}`,
          )}
        </Text>

        {recordedDate ? (
          <Text
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
              fontWeight: "500",
            }}
          >
            {t(`${NS}.recorded_on`, { params: { date: recordedDate } })}
          </Text>
        ) : null}

        {pregnancyStatus ? (
          <View style={{ paddingTop: 2, paddingBottom: 4, gap: 2 }}>
            {/* Not when the heading already says it. A birth is headed "A baby
                was born", and repeating the same sentence two lines down reads
                as a second, separate fact. Every other status has a heading
                that speaks about the result, not about where things stand. */}
            {voice === "birth" ? null : (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 19,
                  color: EMBER.colors.text,
                }}
              >
                {t(`care.update_pregnancy.stands_${pregnancyStatus.stands}`)}
              </Text>
            )}
            {pregnancyStatus.weekCount !== null ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12.5,
                  lineHeight: 18,
                  color: EMBER.colors.textLight,
                }}
              >
                {t(
                  pregnancyStatus.weekCount === 1
                    ? "care.update_pregnancy.weeks_along_one"
                    : "care.update_pregnancy.weeks_along_other",
                  {
                    params: { count: String(pregnancyStatus.weekCount) },
                  },
                )}
              </Text>
            ) : null}
            {pregnancyStatus.liveBirthCount !== null ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12.5,
                  lineHeight: 18,
                  color: EMBER.colors.textLight,
                }}
              >
                {t(
                  pregnancyStatus.liveBirthCount === 1
                    ? "care.update_pregnancy.babies_count_one"
                    : "care.update_pregnancy.babies_count_other",
                  {
                    params: { count: String(pregnancyStatus.liveBirthCount) },
                  },
                )}
              </Text>
            ) : null}
          </View>
        ) : null}

        <View style={{ gap: 10, paddingTop: 2 }}>
          {/* Ending the cycle is the patient's decision, so the partner view
              offers the result but not the close. */}
          {isPartnerView ? null : (
            <CompactActionButton
              testID="close-cycle-trigger"
              label={t(`${NS}.close_cycle`)}
              onPress={onCloseCycle}
              loading={isClosing}
              numberOfLines={2}
            />
          )}
          <CompactActionButton
            testID="update-result-trigger"
            variant="secondary"
            label={t(`${NS}.update_result`)}
            onPress={onUpdateResult}
            numberOfLines={2}
            leading={
              <Pencil size={14} color={EMBER.colors.text} strokeWidth={1.8} />
            }
          />
          {/* A pregnancy is the one ending that keeps changing after it is
              recorded, so it gets its own door. "Update result" stays for a
              repeat beta. */}
          {kind === "pregnancy" && onUpdatePregnancy ? (
            <CompactActionButton
              testID="update-pregnancy-trigger"
              variant="secondary"
              label={t("care.update_pregnancy.specify_the_outcome")}
              onPress={onUpdatePregnancy}
              numberOfLines={2}
              leading={
                <Pencil size={14} color={EMBER.colors.text} strokeWidth={1.8} />
              }
            />
          ) : null}
        </View>

        <Text
          style={{
            fontSize: 11,
            lineHeight: 16,
            color: EMBER.colors.textLight,
            fontWeight: "400",
          }}
        >
          {t(isPartnerView ? `${NS}.partner_hint` : `${NS}.close_hint`)}
        </Text>
      </Animated.View>
    </View>
  );
}
